import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Package, Plus, RefreshCw, X } from "lucide-react";
import type { ExtractedProduct } from "./types";

type Existing = { id: number; name: string };

export type ProductsSectionProps = {
  products: ExtractedProduct[];
  existingProducts: Existing[];
  appliedIdx: Set<number>;
  discardedIdx: Set<number>;
  isPending: boolean;
  onApply: (idx: number, product: ExtractedProduct) => void;
  onDiscard: (idx: number) => void;
};

function findExisting(existing: Existing[], name: string): Existing | undefined {
  const norm = name.trim().toLowerCase();
  return existing.find((p) => p.name.trim().toLowerCase() === norm);
}

export function ProductsSection({
  products,
  existingProducts,
  appliedIdx,
  discardedIdx,
  isPending,
  onApply,
  onDiscard,
}: ProductsSectionProps) {
  const visible = products.map((p, i) => ({ p, i })).filter(({ i }) => !discardedIdx.has(i));
  if (visible.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Package className="w-4 h-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Produtos detectados ({visible.length})</h4>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {visible.map(({ p: product, i: idx }) => {
          const applied = appliedIdx.has(idx);
          const existing = findExisting(existingProducts, product.name);
          const isUpdate = !!existing;
          return (
            <Card key={idx} className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium truncate">{product.name}</p>
                    {isUpdate && !applied && (
                      <Badge variant="outline" className="text-[9px] border-amber-300 bg-amber-50 text-amber-700">
                        já existe
                      </Badge>
                    )}
                  </div>
                  {product.category && (
                    <Badge variant="secondary" className="text-[10px] mt-0.5">{product.category}</Badge>
                  )}
                </div>
                {applied ? (
                  <Badge variant="default" className="shrink-0 bg-emerald-100 text-emerald-700 border-emerald-200">
                    <Check className="w-3 h-3 mr-1" /> {isUpdate ? "Atualizado" : "Criado"}
                  </Badge>
                ) : (
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant={isUpdate ? "default" : "outline"}
                      className="h-7 px-2"
                      onClick={() => onApply(idx, product)}
                      disabled={isPending}
                    >
                      {isUpdate ? (
                        <><RefreshCw className="w-3 h-3 mr-1" /> Atualizar</>
                      ) : (
                        <><Plus className="w-3 h-3 mr-1" /> Criar</>
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground"
                      title="Descartar sugestão"
                      onClick={() => onDiscard(idx)}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              {product.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>
              )}
              {product.valueProposition && (
                <p className="text-[11px] italic text-muted-foreground line-clamp-2">"{product.valueProposition}"</p>
              )}
              {product.differentials && product.differentials.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {product.differentials.slice(0, 4).map((d, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">{d}</Badge>
                  ))}
                </div>
              )}
              {product.price && <p className="text-xs font-medium text-indigo-600">{product.price}</p>}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
